import classNames from "classnames";
import moment from "moment";
import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { ViewProps } from "../ExecutiveDashboard";
import Hint from "../components/Hint";
import { Card, Empty, ErrorState, Kpi, Loading, PendingTag } from "../components/Visuals";
import { date, number, percent, plural, signedDays } from "../format";
import { MilestoneCell, MilestoneProject, MilestonesResponse, Outcome } from "../types";
import { useDashboardData } from "../useDashboardData";
import { Select } from "./Overview";

const OUTCOME_LABELS: { [key in Outcome]: string } = { on_time: "On time", marginal: "Marginal", late: "Late" };
const OUTCOME_RULE = "On time: on or before the agreed date. Marginal: 1 to 5 days late. Late: more than 5 days late.";

type Sort = "newest" | "oldest" | "most_late" | "tenant";
const SORT_LABELS: { [key in Sort]: string } = {
	newest: "Latest activity",
	oldest: "Oldest activity",
	most_late: "Most late first",
	tenant: "Tenant A–Z"
};

const activityDate = (row: MilestoneProject) => row.actual_opening_date || row.latest_actual.date;

const KeyMilestones = ({ asset, properties }: ViewProps) => {
	const { data, isLoading, error } = useDashboardData<MilestonesResponse>("milestones", { property_id: asset ? asset.id : undefined });
	const [query, setQuery] = useState("");
	const [property, setProperty] = useState("");
	const [year, setYear] = useState("");
	const [outcome, setOutcome] = useState("");
	const [type, setType] = useState("");
	const [progress, setProgress] = useState("");
	const [sort, setSort] = useState<Sort>("newest");
	const [selected, setSelected] = useState<MilestoneProject | null>(null);

	if (isLoading) return <Loading />;
	if (error || !data) return <ErrorState />;

	const years = Array.from(new Set(data.projects.map((row) => moment(activityDate(row)).format("YYYY")))).sort().reverse();
	const types = Array.from(new Set(data.projects.map((row) => row.type_label))).sort();
	const needle = query.trim().toLowerCase();

	const rows = data.projects
		.filter((row) => {
			if (property && (!row.property || row.property.id !== property)) return false;
			if (year && moment(activityDate(row)).format("YYYY") !== year) return false;
			if (progress === "completed" && !row.completed) return false;
			if (progress === "in_progress" && row.completed) return false;
			if (outcome && row.opening_outcome !== outcome) return false;
			if (type && row.type_label !== type) return false;
			if (!needle) return true;
			return [row.tenant_name, row.name, row.reference, row.tcode, row.units.join(" ")].join(" ").toLowerCase().includes(needle);
		})
		.sort(sorter(sort));

	const completed = rows.filter((row) => row.completed);
	const inProgress = rows.length - completed.length;
	const withOutcome = completed.filter((row) => row.opening_outcome);
	const onTime = withOutcome.filter((row) => row.opening_outcome === "on_time").length;
	const deltas = completed.filter((row) => row.opening_delta !== null).map((row) => row.opening_delta as number);
	const average = deltas.length ? Math.round(deltas.reduce((sum, value) => sum + value, 0) / deltas.length) : null;

	return (
		<div className="ed-stack">
			<div className="ed-kpis ed-kpis--three">
				<Kpi
					label="Completed projects"
					value={number(completed.length)}
					sub={`Opened up to ${date(data.as_of)} · ${number(inProgress)} in progress`}
					explain={{
						title: "Completed projects",
						lines: ["Projects on or after their Open for Trade date (Date Tenant Opened for Trade on the Opening Certificate task).", `The list also shows ${plural(inProgress, "project")} in progress: at least one actual milestone recorded, not opened yet.`, "Counts the filtered set."]
					}}
				/>
				<Kpi
					label="On time or early"
					value={withOutcome.length ? percent(onTime, withOutcome.length, 0) : "–"}
					valueTone="trading"
					sub={`${number(onTime)} of ${number(withOutcome.length)}`}
					explain={{
						title: "On time or early",
						lines: [
							"Opened on or before the agreed opening date. Marginal openings are not counted.",
							`${number(onTime)} ÷ ${number(withOutcome.length)} completed projects with both dates`,
							completed.length > withOutcome.length && `${plural(completed.length - withOutcome.length, "completed project")} have no agreed opening date.`
						]
					}}
				/>
				<Kpi
					label="Avg opening Δ"
					value={average === null ? "–" : signedDays(average)}
					valueTone={average !== null && average > 0 ? "refurb" : "trading"}
					sub="Across filtered set"
					explain={{
						title: "Average opening Δ",
						lines: ["Actual opening − agreed opening, averaged over completed projects.", `${plural(deltas.length, "project")} with both dates.`]
					}}
				/>
			</div>

			<div className="ed-toolbar ed-toolbar--wrap">
				<input
					className="ed-search"
					type="search"
					placeholder="Search project, tenant, T Code, unit…"
					value={query}
					onChange={(event) => setQuery(event.target.value)}
				/>
				{!asset && (
					<Select
						label="Property"
						value={property}
						options={properties.map((item) => item.id)}
						format={(id) => {
							const match = properties.find((item) => item.id === id);
							return match ? match.short_name : id;
						}}
						onChange={setProperty}
					/>
				)}
				<Select
					label="Status"
					value={progress}
					options={["in_progress", "completed"]}
					format={(value) => (value === "completed" ? "Completed" : "In progress")}
					onChange={setProgress}
				/>
				<Select label="Year" value={year} options={years} onChange={setYear} />
				<Select
					label="Outcome"
					value={outcome}
					options={["on_time", "marginal", "late"]}
					format={(value) => OUTCOME_LABELS[value as Outcome]}
					onChange={setOutcome}
				/>
				<Select label="Type" value={type} options={types} onChange={setType} />
				<label className="ed-select">
					<span className="ed-select__label">Sort:</span>
					<select value={sort} onChange={(event) => setSort(event.target.value as Sort)}>
						{(Object.keys(SORT_LABELS) as Sort[]).map((option) => (
							<option key={option} value={option}>
								{SORT_LABELS[option]}
							</option>
						))}
					</select>
				</label>
			</div>

			<Card className="ed-card--flush">
				{rows.length === 0 ? (
					<Empty>No projects with actual milestone data match.</Empty>
				) : (
					<div className="ed-table-wrap">
						<table className="ed-table ed-table--clickable">
							<thead>
								<tr>
									<th>Project / Tenant</th>
									<th>
										<span className="ed-milestone-codes">
											{data.milestones.map((milestone) => (
												<Hint key={milestone.code} explain={{ lines: [milestone.label] }}>
													<span className="ed-milestone-codes__code">{milestone.code}</span>
												</Hint>
											))}
										</span>
									</th>
									<th className="ed-num">Opening Δ</th>
									<th className="ed-num">Opened / latest</th>
								</tr>
							</thead>
							<tbody>
								{rows.map((row) => (
									<tr key={row.id} onClick={() => setSelected(row)}>
										<td>
											<div className="ed-strong">
												{row.tenant_name}
												{row.project_status === "pending" && <PendingTag />}
											</div>
											{(row.reference || row.tcode || row.name !== row.tenant_name) && (
												<div className="ed-subtext">
													<span className="ed-text--design">{row.reference || (row.name !== row.tenant_name ? row.name : "")}</span>
													{row.tcode ? `${row.reference || row.name !== row.tenant_name ? " · " : ""}${row.tcode}` : ""}
												</div>
											)}
											<div className="ed-subtext">
												{[row.property ? row.property.short_name : null, row.units.join(", ")].filter(Boolean).join(" · ")}
											</div>
										</td>
										<td>
											<span className="ed-milestone-dots">
												{row.milestones.map((cell) => (
													<Hint key={cell.code} explain={cellExplain(cell)}>
														<span className={classNames("ed-milestone-dot", `ed-milestone-dot--${cell.outcome || "none"}`)} />
													</Hint>
												))}
											</span>
										</td>
										<td className="ed-num">
											{row.completed ? (
												<Hint align="right" explain={openingExplain(row)}>
													<div className={classNames("ed-delta", row.opening_outcome && `ed-delta--${row.opening_outcome}`)}>
														{signedDays(row.opening_delta)}
													</div>
													{row.opening_outcome && <OutcomeBadge outcome={row.opening_outcome} />}
												</Hint>
											) : (
												<span className="ed-muted">Not opened</span>
											)}
										</td>
										<td className="ed-num">
											{row.completed ? (
												<React.Fragment>
													<div className="ed-strong">{date(row.actual_opening_date)}</div>
													<div className="ed-subtext">Actual opening</div>
												</React.Fragment>
											) : (
												<Hint
													align="right"
													explain={{
														title: "In progress",
														lines: [`${plural(row.recorded_count, "actual milestone")} recorded so far.`, `Latest: ${row.latest_actual.label} on ${date(row.latest_actual.date)}`]
													}}
												>
													<div className="ed-strong ed-text--design">In progress</div>
													<div className="ed-subtext">
														{row.latest_actual.code} · {date(row.latest_actual.date)}
													</div>
												</Hint>
											)}
										</td>
									</tr>
								))}
							</tbody>
						</table>
					</div>
				)}
			</Card>

			{selected && <MilestoneDrawer project={selected} onClose={() => setSelected(null)} />}
		</div>
	);
};

const MilestoneDrawer = ({ project, onClose }: { project: MilestoneProject; onClose: () => void }) => {
	useEffect(() => {
		const onKey = (event: KeyboardEvent) => {
			if (event.key === "Escape") onClose();
		};
		document.addEventListener("keydown", onKey);
		return () => document.removeEventListener("keydown", onKey);
	}, [onClose]);

	return createPortal(
		<div className="ed-drawer" role="dialog" aria-label={`${project.tenant_name} milestones`}>
			<div className="ed-drawer__backdrop" onClick={onClose} />
			<div className="ed-drawer__panel">
				<header className="ed-drawer__header">
					<div>
						<div className="ed-eyebrow">
							{project.completed ? "Completed project" : "In progress"}
							{project.reference ? ` · ${project.reference}` : ""}
						</div>
						<h2 className="ed-card__title">{project.tenant_name}</h2>
						<div className="ed-subtext">
							{[project.tcode, project.property ? project.property.name : null, project.units.join(", ")].filter(Boolean).join(" · ")}
						</div>
					</div>
					<button type="button" className="ed-drawer__close" onClick={onClose} aria-label="Close">
						×
					</button>
				</header>

				{project.completed ? (
					<div className={classNames("ed-outcome-box", `ed-outcome-box--${project.opening_outcome || "none"}`)}>
						<div className="ed-eyebrow">Opening outcome</div>
						<div className="ed-outcome-box__value">
							{signedDays(project.opening_delta)}
							{project.opening_outcome ? ` · ${OUTCOME_LABELS[project.opening_outcome]}` : ""}
						</div>
						<div className="ed-subtext">
							Forecast {date(project.agreed_opening_date)} → Actual {date(project.actual_opening_date)}
						</div>
					</div>
				) : (
					<div className="ed-outcome-box">
						<div className="ed-eyebrow">Not opened yet</div>
						<div className="ed-outcome-box__value ed-text--design">
							Latest: {project.latest_actual.label} · {date(project.latest_actual.date)}
						</div>
						<div className="ed-subtext">Forecast opening {date(project.agreed_opening_date)}</div>
					</div>
				)}

				<div className="ed-eyebrow">Milestone slip</div>
				<div className="ed-slip">
					{project.milestones.map((cell) => (
						<Hint key={cell.code} block className="ed-slip__item" explain={cellExplain(cell)}>
							<span className="ed-slip__code">{cell.code}</span>
							<span className={classNames("ed-slip__track", `ed-slip__track--${cell.outcome || "none"}`)}>
								<span className="ed-slip__from" />
								<span className="ed-slip__to" />
							</span>
							<span className={classNames("ed-delta", cell.outcome && `ed-delta--${cell.outcome}`)}>{signedDays(cell.delta)}</span>
						</Hint>
					))}
				</div>

				<div className="ed-eyebrow">Forecast vs actual</div>
				<table className="ed-table ed-table--compact">
					<thead>
						<tr>
							<th>Milestone</th>
							<th>Forecast</th>
							<th>Actual</th>
							<th className="ed-num">Δ</th>
							<th>Outcome</th>
						</tr>
					</thead>
					<tbody>
						{project.milestones.map((cell) => (
							<tr key={cell.code}>
								<td>
									{cell.label}
									{cell.landlord && <span className="ed-landlord">Landlord</span>}
								</td>
								<td className="ed-muted">{date(cell.agreed)}</td>
								<td>{date(cell.actual)}</td>
								<td className={classNames("ed-num ed-strong", cell.outcome && `ed-delta--${cell.outcome}`)}>{signedDays(cell.delta)}</td>
								<td>{cell.outcome ? <OutcomeBadge outcome={cell.outcome} /> : <span className="ed-muted">No data</span>}</td>
							</tr>
						))}
					</tbody>
				</table>
				<p className="ed-note">{OUTCOME_RULE} Forecast dates are the agreed dates TCPinpoint sends to AFG; actual dates come from the workflow tasks.</p>
				<Link to={`/projects/${project.id}`} className="ed-button">
					Open project
				</Link>
			</div>
		</div>,
		document.body
	);
};

const OutcomeBadge = ({ outcome }: { outcome: Outcome }) => (
	<span className={classNames("ed-badge", `ed-badge--outcome-${outcome}`)}>
		<span className="ed-badge__dot" />
		{OUTCOME_LABELS[outcome]}
	</span>
);

const cellExplain = (cell: MilestoneCell) => ({
	title: cell.label,
	lines: [
		`Forecast: ${date(cell.agreed)} · Actual: ${date(cell.actual)}`,
		cell.delta !== null ? `Δ ${signedDays(cell.delta)} → ${OUTCOME_LABELS[cell.outcome as Outcome]}` : "Not compared: a date is missing.",
		OUTCOME_RULE
	]
});

const openingExplain = (row: MilestoneProject) => ({
	title: "Opening Δ",
	lines: [
		`Actual ${date(row.actual_opening_date)} − agreed ${date(row.agreed_opening_date)}`,
		row.opening_delta === null ? "No agreed opening date from Yardi." : `= ${signedDays(row.opening_delta)}`,
		OUTCOME_RULE
	]
});

const sorter = (sort: Sort) => (a: MilestoneProject, b: MilestoneProject) => {
	switch (sort) {
		case "oldest":
			return activityDate(a).localeCompare(activityDate(b));
		case "most_late":
			return (b.opening_delta === null ? -99999 : b.opening_delta) - (a.opening_delta === null ? -99999 : a.opening_delta);
		case "tenant":
			return a.tenant_name.localeCompare(b.tenant_name);
		default:
			return activityDate(b).localeCompare(activityDate(a));
	}
};

export default KeyMilestones;
